/**
 * Permission Types and Constants
 * Centralized source of truth for all permissions in the system
 * Must match backend schema: organization-roles.schema.js
 */

// ============================================================================
// SECTION ACCESS PERMISSIONS
// ============================================================================

export const SECTION_ACCESS_PERMISSIONS = {
  CRM: "can_access_crm",
  OPERATIONS: "can_access_operations",
  HELPDESK: "can_access_helpdesk",
  FINANCE: "can_access_finance",
  SITES: "can_access_sites",
  PRESENTATIONS: "can_access_presentations",
  CONTRACTS: "can_access_contracts",
  WHITEBOARDS: "can_access_whiteboards",
  EVENTS: "can_access_events",
  RESERVATIONS: "can_access_reservations",
  MEETINGS: "can_access_meetings",
  LINKS: "can_access_links",
  MARKETPLACE: "can_access_marketplace",
} as const;

// ============================================================================
// MODULE ACCESS PERMISSIONS
// ============================================================================

export const MODULE_ACCESS_PERMISSIONS = {
  // CRM modules
  CONVERSATIONS: "can_access_conversations",
  DEALS: "can_access_deals",
  TICKETS: "can_access_tickets",
  HELPDESK: "can_access_helpdesk",
  ACTIVITIES: "can_access_activities",
  RESERVATIONS: "can_access_reservations",
  CONTACTS: "can_access_contacts",
  COMPANIES: "can_access_companies",
  PRODUCTS: "can_access_products",
  INVOICES: "can_access_invoices",
  ESTIMATES: "can_access_estimates",
  ORDERS: "can_access_orders",
  PAYMENTS: "can_access_payments",
  VENDORS: "can_access_vendors",
  EXPENSES: "can_access_expenses",
  BILLS: "can_access_bills",
  VENDOR_PAYMENTS: "can_access_vendor_payments",
  FINANCE_SCENARIOS: "can_access_finance_scenarios",
  CAMPAIGNS: "can_access_campaigns",
  AI_ASSISTANT: "can_access_ai_assistant",
  AUTOMATIONS: "can_access_automations",
  ANALYTICS: "can_access_analytics",
  EVENTS: "can_access_events",
  CONTRACTS: "can_access_contracts",
  WHITEBOARDS: "can_access_whiteboards",
  // Operations modules
  STRATEGY: "can_access_strategy",
  PROJECTS: "can_access_projects",
  TASKS: "can_access_tasks",
  TEAMS: "can_access_teams",
  CALENDAR: "can_access_calendar",
  REPORTS: "can_access_reports",
} as const;

// ============================================================================
// GRANULAR PERMISSIONS BY MODULE
// ============================================================================

export const CONVERSATIONS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_conversations",
  VIEW_ALL: "can_view_all_conversations",
  CREATE: "can_create_conversations",
  EDIT_OWN: "can_edit_own_conversations",
  EDIT_ALL: "can_edit_all_conversations",
  DELETE_OWN: "can_delete_own_conversations",
  DELETE_ALL: "can_delete_all_conversations",
} as const;

export const DEALS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_deals",
  VIEW_ALL: "can_view_all_deals",
  CREATE: "can_create_deals",
  EDIT_OWN: "can_edit_own_deals",
  EDIT_ALL: "can_edit_all_deals",
  DELETE_OWN: "can_delete_own_deals",
  DELETE_ALL: "can_delete_all_deals",
} as const;

export const ACTIVITIES_PERMISSIONS = {
  VIEW_OWN: "can_view_own_activities",
  VIEW_ALL: "can_view_all_activities",
  CREATE: "can_create_activities",
  EDIT_OWN: "can_edit_own_activities",
  EDIT_ALL: "can_edit_all_activities",
  DELETE_OWN: "can_delete_own_activities",
  DELETE_ALL: "can_delete_all_activities",
} as const;

export const RESERVATIONS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_reservations",
  VIEW_ALL: "can_view_all_reservations",
  CREATE: "can_create_reservations",
  EDIT_OWN: "can_edit_own_reservations",
  EDIT_ALL: "can_edit_all_reservations",
  DELETE_OWN: "can_delete_own_reservations",
  DELETE_ALL: "can_delete_all_reservations",
} as const;

export const EVENTS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_events",
  VIEW_ALL: "can_view_all_events",
  CREATE: "can_create_events",
  EDIT_OWN: "can_edit_own_events",
  EDIT_ALL: "can_edit_all_events",
  DELETE_OWN: "can_delete_own_events",
  DELETE_ALL: "can_delete_all_events",
} as const;

export const CONTRACTS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_contracts",
  VIEW_ALL: "can_view_all_contracts",
  CREATE: "can_create_contracts",
  EDIT_OWN: "can_edit_own_contracts",
  EDIT_ALL: "can_edit_all_contracts",
  DELETE_OWN: "can_delete_own_contracts",
  DELETE_ALL: "can_delete_all_contracts",
} as const;

export const CONTACTS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_contacts",
  VIEW_ALL: "can_view_all_contacts",
  CREATE: "can_create_contacts",
  EDIT_OWN: "can_edit_own_contacts",
  EDIT_ALL: "can_edit_all_contacts",
  DELETE_OWN: "can_delete_own_contacts",
  DELETE_ALL: "can_delete_all_contacts",
} as const;

export const COMPANIES_PERMISSIONS = {
  VIEW_OWN: "can_view_own_companies",
  VIEW_ALL: "can_view_all_companies",
  CREATE: "can_create_companies",
  EDIT_OWN: "can_edit_own_companies",
  EDIT_ALL: "can_edit_all_companies",
  DELETE_OWN: "can_delete_own_companies",
  DELETE_ALL: "can_delete_all_companies",
} as const;

export const TICKETS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_tickets",
  VIEW_ALL: "can_view_all_tickets",
  CREATE: "can_create_tickets",
  EDIT_OWN: "can_edit_own_tickets",
  EDIT_ALL: "can_edit_all_tickets",
  DELETE_OWN: "can_delete_own_tickets",
  DELETE_ALL: "can_delete_all_tickets",
} as const;

export const INVOICES_PERMISSIONS = {
  VIEW_OWN: "can_view_own_invoices",
  VIEW_ALL: "can_view_all_invoices",
  CREATE: "can_create_invoices",
  EDIT_OWN: "can_edit_own_invoices",
  EDIT_ALL: "can_edit_all_invoices",
  DELETE_OWN: "can_delete_own_invoices",
  DELETE_ALL: "can_delete_all_invoices",
} as const;

export const ESTIMATES_PERMISSIONS = {
  VIEW_OWN: "can_view_own_estimates",
  VIEW_ALL: "can_view_all_estimates",
  CREATE: "can_create_estimates",
  EDIT_OWN: "can_edit_own_estimates",
  EDIT_ALL: "can_edit_all_estimates",
  DELETE_OWN: "can_delete_own_estimates",
  DELETE_ALL: "can_delete_all_estimates",
} as const;

export const ORDERS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_orders",
  VIEW_ALL: "can_view_all_orders",
  CREATE: "can_create_orders",
  EDIT_OWN: "can_edit_own_orders",
  EDIT_ALL: "can_edit_all_orders",
  DELETE_OWN: "can_delete_own_orders",
  DELETE_ALL: "can_delete_all_orders",
} as const;

export const VENDORS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_vendors",
  VIEW_ALL: "can_view_all_vendors",
  CREATE: "can_create_vendors",
  EDIT_OWN: "can_edit_own_vendors",
  EDIT_ALL: "can_edit_all_vendors",
  DELETE_OWN: "can_delete_own_vendors",
  DELETE_ALL: "can_delete_all_vendors",
} as const;

export const FINANCE_SCENARIOS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_finance_scenarios",
  VIEW_ALL: "can_view_all_finance_scenarios",
  CREATE: "can_create_finance_scenarios",
  EDIT_OWN: "can_edit_own_finance_scenarios",
  EDIT_ALL: "can_edit_all_finance_scenarios",
  DELETE_OWN: "can_delete_own_finance_scenarios",
  DELETE_ALL: "can_delete_all_finance_scenarios",
} as const;

export const EXPENSES_PERMISSIONS = {
  VIEW_OWN: "can_view_own_expenses",
  VIEW_ALL: "can_view_all_expenses",
  CREATE: "can_create_expenses",
  EDIT_OWN: "can_edit_own_expenses",
  EDIT_ALL: "can_edit_all_expenses",
  DELETE_OWN: "can_delete_own_expenses",
  DELETE_ALL: "can_delete_all_expenses",
} as const;

export const BILLS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_bills",
  VIEW_ALL: "can_view_all_bills",
  CREATE: "can_create_bills",
  EDIT_OWN: "can_edit_own_bills",
  EDIT_ALL: "can_edit_all_bills",
  DELETE_OWN: "can_delete_own_bills",
  DELETE_ALL: "can_delete_all_bills",
} as const;

export const VENDOR_PAYMENTS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_vendor_payments",
  VIEW_ALL: "can_view_all_vendor_payments",
  CREATE: "can_create_vendor_payments",
  EDIT_OWN: "can_edit_own_vendor_payments",
  EDIT_ALL: "can_edit_all_vendor_payments",
  DELETE_OWN: "can_delete_own_vendor_payments",
  DELETE_ALL: "can_delete_all_vendor_payments",
} as const;

export const STRATEGY_PERMISSIONS = {
  VIEW_OWN: "can_view_own_strategy",
  VIEW_ALL: "can_view_all_strategy",
  CREATE: "can_create_strategy",
  EDIT_OWN: "can_edit_own_strategy",
  EDIT_ALL: "can_edit_all_strategy",
  DELETE_OWN: "can_delete_own_strategy",
  DELETE_ALL: "can_delete_all_strategy",
} as const;

export const COLLECTIONS_PERMISSIONS = {
  CREATE: "can_create_collections",
  EDIT: "can_edit_collections",
} as const;

export const PROJECTS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_projects",
  VIEW_ALL: "can_view_all_projects",
  CREATE: "can_create_projects",
  EDIT_OWN: "can_edit_own_projects",
  EDIT_ALL: "can_edit_all_projects",
  DELETE_OWN: "can_delete_own_projects",
  DELETE_ALL: "can_delete_all_projects",
} as const;

export const WHITEBOARDS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_whiteboards",
  VIEW_ALL: "can_view_all_whiteboards",
} as const;

export const PRESENTATIONS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_presentations",
  VIEW_ALL: "can_view_all_presentations",
} as const;

export const HELPDESK_PERMISSIONS = {
  MANAGE_SETTINGS: "can_manage_helpdesk_settings",
} as const;

export const HELPDESK_TICKET_SCOPE_KEY = "helpdesk_ticket_scope";

export const HELPDESK_TICKET_SCOPES = {
  ALL: "all",
  GROUP: "group",
  ASSIGNED: "assigned",
} as const;

export type HelpdeskTicketScope =
  (typeof HELPDESK_TICKET_SCOPES)[keyof typeof HELPDESK_TICKET_SCOPES];

export const TASKS_PERMISSIONS = {
  VIEW_OWN: "can_view_own_tasks",
  VIEW_ALL: "can_view_all_tasks",
  CREATE: "can_create_tasks",
  EDIT_OWN: "can_edit_own_tasks",
  EDIT_ALL: "can_edit_all_tasks",
  DELETE_OWN: "can_delete_own_tasks",
  DELETE_ALL: "can_delete_all_tasks",
} as const;

// ============================================================================
// ALL PERMISSIONS COMBINED
// ============================================================================

export const PERMISSIONS = {
  ...SECTION_ACCESS_PERMISSIONS,
  ...MODULE_ACCESS_PERMISSIONS,
  ...CONVERSATIONS_PERMISSIONS,
  ...DEALS_PERMISSIONS,
  ...ACTIVITIES_PERMISSIONS,
  ...RESERVATIONS_PERMISSIONS,
  ...EVENTS_PERMISSIONS,
  ...CONTRACTS_PERMISSIONS,
  ...CONTACTS_PERMISSIONS,
  ...COMPANIES_PERMISSIONS,
  ...TICKETS_PERMISSIONS,
  ...INVOICES_PERMISSIONS,
  ...ESTIMATES_PERMISSIONS,
  ...ORDERS_PERMISSIONS,
  ...VENDORS_PERMISSIONS,
  ...EXPENSES_PERMISSIONS,
  ...BILLS_PERMISSIONS,
  ...VENDOR_PAYMENTS_PERMISSIONS,
  ...FINANCE_SCENARIOS_PERMISSIONS,
  ...STRATEGY_PERMISSIONS,
  ...COLLECTIONS_PERMISSIONS,
  ...PROJECTS_PERMISSIONS,
  ...TASKS_PERMISSIONS,
  ...HELPDESK_PERMISSIONS,
} as const;

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type SectionAccessKey =
  (typeof SECTION_ACCESS_PERMISSIONS)[keyof typeof SECTION_ACCESS_PERMISSIONS];
export type ModuleAccessKey =
  (typeof MODULE_ACCESS_PERMISSIONS)[keyof typeof MODULE_ACCESS_PERMISSIONS];
export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type UserRole = "owner" | "admin" | "member";

export interface OrganizationPermissions {
  [key: string]: boolean | string | undefined;

  // Section Access
  can_access_crm?: boolean;
  can_access_operations?: boolean;
  can_access_helpdesk?: boolean;
  can_access_finance?: boolean;
  can_access_sites?: boolean;
  can_access_presentations?: boolean;
  can_access_contracts?: boolean;
  can_access_whiteboards?: boolean;
  can_access_events?: boolean;
  can_access_meetings?: boolean;
  can_access_links?: boolean;
  can_access_marketplace?: boolean;

  // CRM Module Access Permissions
  can_access_conversations?: boolean;
  can_access_deals?: boolean;
  can_access_tickets?: boolean;
  can_access_activities?: boolean;
  can_access_contacts?: boolean;
  can_access_companies?: boolean;
  can_access_products?: boolean;
  can_access_invoices?: boolean;
  can_access_estimates?: boolean;
  can_access_orders?: boolean;
  can_access_payments?: boolean;
  can_access_vendors?: boolean;
  can_access_expenses?: boolean;
  can_access_bills?: boolean;
  can_access_vendor_payments?: boolean;
  can_access_finance_scenarios?: boolean;
  can_access_campaigns?: boolean;
  can_access_ai_assistant?: boolean;
  can_access_automations?: boolean;
  can_access_analytics?: boolean;
  can_access_reservations?: boolean;

  // Whiteboards Visibility Permissions (membership-based, no CRUD split)
  can_view_own_whiteboards?: boolean;
  can_view_all_whiteboards?: boolean;
  can_view_own_presentations?: boolean;
  can_view_all_presentations?: boolean;

  // Operations Module Access Permissions
  can_access_strategy?: boolean;
  can_access_projects?: boolean;
  can_access_tasks?: boolean;
  can_access_teams?: boolean;
  can_access_calendar?: boolean;
  can_access_reports?: boolean;

  // Conversations Granular Permissions
  can_view_own_conversations?: boolean;
  can_view_all_conversations?: boolean;
  can_create_conversations?: boolean;
  can_edit_own_conversations?: boolean;
  can_edit_all_conversations?: boolean;
  can_delete_own_conversations?: boolean;
  can_delete_all_conversations?: boolean;

  // Deals Granular Permissions
  can_view_own_deals?: boolean;
  can_view_all_deals?: boolean;
  can_create_deals?: boolean;
  can_edit_own_deals?: boolean;
  can_edit_all_deals?: boolean;
  can_delete_own_deals?: boolean;
  can_delete_all_deals?: boolean;

  // Activities Granular Permissions
  can_view_own_activities?: boolean;
  can_view_all_activities?: boolean;
  can_create_activities?: boolean;
  can_edit_own_activities?: boolean;
  can_edit_all_activities?: boolean;
  can_delete_own_activities?: boolean;
  can_delete_all_activities?: boolean;

  // Reservations Granular Permissions
  can_view_own_reservations?: boolean;
  can_view_all_reservations?: boolean;
  can_create_reservations?: boolean;
  can_edit_own_reservations?: boolean;
  can_edit_all_reservations?: boolean;
  can_delete_own_reservations?: boolean;
  can_delete_all_reservations?: boolean;

  // Events Granular Permissions
  can_view_own_events?: boolean;
  can_view_all_events?: boolean;
  can_create_events?: boolean;
  can_edit_own_events?: boolean;
  can_edit_all_events?: boolean;
  can_delete_own_events?: boolean;
  can_delete_all_events?: boolean;

  // Contracts Granular Permissions
  can_view_own_contracts?: boolean;
  can_view_all_contracts?: boolean;
  can_create_contracts?: boolean;
  can_edit_own_contracts?: boolean;
  can_edit_all_contracts?: boolean;
  can_delete_own_contracts?: boolean;
  can_delete_all_contracts?: boolean;

  // Contacts Granular Permissions
  can_view_own_contacts?: boolean;
  can_view_all_contacts?: boolean;
  can_create_contacts?: boolean;
  can_edit_own_contacts?: boolean;
  can_edit_all_contacts?: boolean;
  can_delete_own_contacts?: boolean;
  can_delete_all_contacts?: boolean;

  // Companies Granular Permissions
  can_view_own_companies?: boolean;
  can_view_all_companies?: boolean;
  can_create_companies?: boolean;
  can_edit_own_companies?: boolean;
  can_edit_all_companies?: boolean;
  can_delete_own_companies?: boolean;
  can_delete_all_companies?: boolean;

  // Tickets Granular Permissions
  can_view_own_tickets?: boolean;
  can_view_all_tickets?: boolean;
  can_create_tickets?: boolean;
  can_edit_own_tickets?: boolean;
  can_edit_all_tickets?: boolean;
  can_delete_own_tickets?: boolean;
  can_delete_all_tickets?: boolean;

  // Invoices Granular Permissions
  can_view_own_invoices?: boolean;
  can_view_all_invoices?: boolean;
  can_create_invoices?: boolean;
  can_edit_own_invoices?: boolean;
  can_edit_all_invoices?: boolean;
  can_delete_own_invoices?: boolean;
  can_delete_all_invoices?: boolean;

  // Estimates Granular Permissions
  can_view_own_estimates?: boolean;
  can_view_all_estimates?: boolean;
  can_create_estimates?: boolean;
  can_edit_own_estimates?: boolean;
  can_edit_all_estimates?: boolean;
  can_delete_own_estimates?: boolean;
  can_delete_all_estimates?: boolean;

  // Orders Granular Permissions
  can_view_own_orders?: boolean;
  can_view_all_orders?: boolean;
  can_create_orders?: boolean;
  can_edit_own_orders?: boolean;
  can_edit_all_orders?: boolean;
  can_delete_own_orders?: boolean;
  can_delete_all_orders?: boolean;

  // Vendors Granular Permissions
  can_view_own_vendors?: boolean;
  can_view_all_vendors?: boolean;
  can_create_vendors?: boolean;
  can_edit_own_vendors?: boolean;
  can_edit_all_vendors?: boolean;
  can_delete_own_vendors?: boolean;
  can_delete_all_vendors?: boolean;

  // Expenses Granular Permissions
  can_view_own_expenses?: boolean;
  can_view_all_expenses?: boolean;
  can_create_expenses?: boolean;
  can_edit_own_expenses?: boolean;
  can_edit_all_expenses?: boolean;
  can_delete_own_expenses?: boolean;
  can_delete_all_expenses?: boolean;

  // Bills Granular Permissions
  can_view_own_bills?: boolean;
  can_view_all_bills?: boolean;
  can_create_bills?: boolean;
  can_edit_own_bills?: boolean;
  can_edit_all_bills?: boolean;
  can_delete_own_bills?: boolean;
  can_delete_all_bills?: boolean;

  // Vendor Payments Granular Permissions
  can_view_own_vendor_payments?: boolean;
  can_view_all_vendor_payments?: boolean;
  can_create_vendor_payments?: boolean;
  can_edit_own_vendor_payments?: boolean;
  can_edit_all_vendor_payments?: boolean;
  can_delete_own_vendor_payments?: boolean;
  can_delete_all_vendor_payments?: boolean;
  can_view_own_finance_scenarios?: boolean;
  can_view_all_finance_scenarios?: boolean;
  can_create_finance_scenarios?: boolean;
  can_edit_own_finance_scenarios?: boolean;
  can_edit_all_finance_scenarios?: boolean;
  can_delete_own_finance_scenarios?: boolean;
  can_delete_all_finance_scenarios?: boolean;

  // Strategy Granular Permissions
  can_view_own_strategy?: boolean;
  can_view_all_strategy?: boolean;
  can_create_strategy?: boolean;
  can_edit_own_strategy?: boolean;
  can_edit_all_strategy?: boolean;
  can_delete_own_strategy?: boolean;
  can_delete_all_strategy?: boolean;

  can_create_collections?: boolean;
  can_edit_collections?: boolean;

  // Projects Granular Permissions
  can_view_own_projects?: boolean;
  can_view_all_projects?: boolean;
  can_create_projects?: boolean;
  can_edit_own_projects?: boolean;
  can_edit_all_projects?: boolean;
  can_delete_own_projects?: boolean;
  can_delete_all_projects?: boolean;

  // Tasks Granular Permissions
  can_view_own_tasks?: boolean;
  can_view_all_tasks?: boolean;
  can_create_tasks?: boolean;
  can_edit_own_tasks?: boolean;
  can_edit_all_tasks?: boolean;
  can_delete_own_tasks?: boolean;
  can_delete_all_tasks?: boolean;

  // Helpdesk Granular Permissions
  helpdesk_ticket_scope?: HelpdeskTicketScope;
  can_manage_helpdesk_settings?: boolean;
}

// ============================================================================
// PERMISSION CONFIGURATION FOR UI
// ============================================================================

export interface ModulePermissionConfig {
  view_own?: string;
  view_all?: string;
  create?: string;
  edit_own?: string;
  edit_all?: string;
  delete_own?: string;
  delete_all?: string;
}

export interface ModuleConfig {
  name: string;
  accessKey: ModuleAccessKey;
  hasOwnConcept: boolean;
  permissions: ModulePermissionConfig;
}

export const CRM_MODULES: ModuleConfig[] = [
  {
    name: "Conversations",
    accessKey: MODULE_ACCESS_PERMISSIONS.CONVERSATIONS,
    hasOwnConcept: true,
    permissions: {
      view_own: CONVERSATIONS_PERMISSIONS.VIEW_OWN,
      view_all: CONVERSATIONS_PERMISSIONS.VIEW_ALL,
      create: CONVERSATIONS_PERMISSIONS.CREATE,
      edit_own: CONVERSATIONS_PERMISSIONS.EDIT_OWN,
      edit_all: CONVERSATIONS_PERMISSIONS.EDIT_ALL,
      delete_own: CONVERSATIONS_PERMISSIONS.DELETE_OWN,
      delete_all: CONVERSATIONS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Deals",
    accessKey: MODULE_ACCESS_PERMISSIONS.DEALS,
    hasOwnConcept: true,
    permissions: {
      view_own: DEALS_PERMISSIONS.VIEW_OWN,
      view_all: DEALS_PERMISSIONS.VIEW_ALL,
      create: DEALS_PERMISSIONS.CREATE,
      edit_own: DEALS_PERMISSIONS.EDIT_OWN,
      edit_all: DEALS_PERMISSIONS.EDIT_ALL,
      delete_own: DEALS_PERMISSIONS.DELETE_OWN,
      delete_all: DEALS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Activities",
    accessKey: MODULE_ACCESS_PERMISSIONS.ACTIVITIES,
    hasOwnConcept: true,
    permissions: {
      view_own: ACTIVITIES_PERMISSIONS.VIEW_OWN,
      view_all: ACTIVITIES_PERMISSIONS.VIEW_ALL,
      create: ACTIVITIES_PERMISSIONS.CREATE,
      edit_own: ACTIVITIES_PERMISSIONS.EDIT_OWN,
      edit_all: ACTIVITIES_PERMISSIONS.EDIT_ALL,
      delete_own: ACTIVITIES_PERMISSIONS.DELETE_OWN,
      delete_all: ACTIVITIES_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Reservations",
    accessKey: MODULE_ACCESS_PERMISSIONS.RESERVATIONS,
    hasOwnConcept: true,
    permissions: {
      view_own: RESERVATIONS_PERMISSIONS.VIEW_OWN,
      view_all: RESERVATIONS_PERMISSIONS.VIEW_ALL,
      create: RESERVATIONS_PERMISSIONS.CREATE,
      edit_own: RESERVATIONS_PERMISSIONS.EDIT_OWN,
      edit_all: RESERVATIONS_PERMISSIONS.EDIT_ALL,
      delete_own: RESERVATIONS_PERMISSIONS.DELETE_OWN,
      delete_all: RESERVATIONS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Contacts",
    accessKey: MODULE_ACCESS_PERMISSIONS.CONTACTS,
    hasOwnConcept: true,
    permissions: {
      view_own: CONTACTS_PERMISSIONS.VIEW_OWN,
      view_all: CONTACTS_PERMISSIONS.VIEW_ALL,
      create: CONTACTS_PERMISSIONS.CREATE,
      edit_own: CONTACTS_PERMISSIONS.EDIT_OWN,
      edit_all: CONTACTS_PERMISSIONS.EDIT_ALL,
      delete_own: CONTACTS_PERMISSIONS.DELETE_OWN,
      delete_all: CONTACTS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Companies",
    accessKey: MODULE_ACCESS_PERMISSIONS.COMPANIES,
    hasOwnConcept: true,
    permissions: {
      view_own: COMPANIES_PERMISSIONS.VIEW_OWN,
      view_all: COMPANIES_PERMISSIONS.VIEW_ALL,
      create: COMPANIES_PERMISSIONS.CREATE,
      edit_own: COMPANIES_PERMISSIONS.EDIT_OWN,
      edit_all: COMPANIES_PERMISSIONS.EDIT_ALL,
      delete_own: COMPANIES_PERMISSIONS.DELETE_OWN,
      delete_all: COMPANIES_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Tickets",
    accessKey: MODULE_ACCESS_PERMISSIONS.TICKETS,
    hasOwnConcept: true,
    permissions: {
      view_own: TICKETS_PERMISSIONS.VIEW_OWN,
      view_all: TICKETS_PERMISSIONS.VIEW_ALL,
      create: TICKETS_PERMISSIONS.CREATE,
      edit_own: TICKETS_PERMISSIONS.EDIT_OWN,
      edit_all: TICKETS_PERMISSIONS.EDIT_ALL,
      delete_own: TICKETS_PERMISSIONS.DELETE_OWN,
      delete_all: TICKETS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Invoices",
    accessKey: MODULE_ACCESS_PERMISSIONS.INVOICES,
    hasOwnConcept: true,
    permissions: {
      view_own: INVOICES_PERMISSIONS.VIEW_OWN,
      view_all: INVOICES_PERMISSIONS.VIEW_ALL,
      create: INVOICES_PERMISSIONS.CREATE,
      edit_own: INVOICES_PERMISSIONS.EDIT_OWN,
      edit_all: INVOICES_PERMISSIONS.EDIT_ALL,
      delete_own: INVOICES_PERMISSIONS.DELETE_OWN,
      delete_all: INVOICES_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Estimates",
    accessKey: MODULE_ACCESS_PERMISSIONS.ESTIMATES,
    hasOwnConcept: true,
    permissions: {
      view_own: ESTIMATES_PERMISSIONS.VIEW_OWN,
      view_all: ESTIMATES_PERMISSIONS.VIEW_ALL,
      create: ESTIMATES_PERMISSIONS.CREATE,
      edit_own: ESTIMATES_PERMISSIONS.EDIT_OWN,
      edit_all: ESTIMATES_PERMISSIONS.EDIT_ALL,
      delete_own: ESTIMATES_PERMISSIONS.DELETE_OWN,
      delete_all: ESTIMATES_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Orders",
    accessKey: MODULE_ACCESS_PERMISSIONS.ORDERS,
    hasOwnConcept: true,
    permissions: {
      view_own: ORDERS_PERMISSIONS.VIEW_OWN,
      view_all: ORDERS_PERMISSIONS.VIEW_ALL,
      create: ORDERS_PERMISSIONS.CREATE,
      edit_own: ORDERS_PERMISSIONS.EDIT_OWN,
      edit_all: ORDERS_PERMISSIONS.EDIT_ALL,
      delete_own: ORDERS_PERMISSIONS.DELETE_OWN,
      delete_all: ORDERS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Vendors",
    accessKey: MODULE_ACCESS_PERMISSIONS.VENDORS,
    hasOwnConcept: true,
    permissions: {
      view_own: VENDORS_PERMISSIONS.VIEW_OWN,
      view_all: VENDORS_PERMISSIONS.VIEW_ALL,
      create: VENDORS_PERMISSIONS.CREATE,
      edit_own: VENDORS_PERMISSIONS.EDIT_OWN,
      edit_all: VENDORS_PERMISSIONS.EDIT_ALL,
      delete_own: VENDORS_PERMISSIONS.DELETE_OWN,
      delete_all: VENDORS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Expenses",
    accessKey: MODULE_ACCESS_PERMISSIONS.EXPENSES,
    hasOwnConcept: true,
    permissions: {
      view_own: EXPENSES_PERMISSIONS.VIEW_OWN,
      view_all: EXPENSES_PERMISSIONS.VIEW_ALL,
      create: EXPENSES_PERMISSIONS.CREATE,
      edit_own: EXPENSES_PERMISSIONS.EDIT_OWN,
      edit_all: EXPENSES_PERMISSIONS.EDIT_ALL,
      delete_own: EXPENSES_PERMISSIONS.DELETE_OWN,
      delete_all: EXPENSES_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Bills",
    accessKey: MODULE_ACCESS_PERMISSIONS.BILLS,
    hasOwnConcept: true,
    permissions: {
      view_own: BILLS_PERMISSIONS.VIEW_OWN,
      view_all: BILLS_PERMISSIONS.VIEW_ALL,
      create: BILLS_PERMISSIONS.CREATE,
      edit_own: BILLS_PERMISSIONS.EDIT_OWN,
      edit_all: BILLS_PERMISSIONS.EDIT_ALL,
      delete_own: BILLS_PERMISSIONS.DELETE_OWN,
      delete_all: BILLS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Vendor Payments",
    accessKey: MODULE_ACCESS_PERMISSIONS.VENDOR_PAYMENTS,
    hasOwnConcept: true,
    permissions: {
      view_own: VENDOR_PAYMENTS_PERMISSIONS.VIEW_OWN,
      view_all: VENDOR_PAYMENTS_PERMISSIONS.VIEW_ALL,
      create: VENDOR_PAYMENTS_PERMISSIONS.CREATE,
      edit_own: VENDOR_PAYMENTS_PERMISSIONS.EDIT_OWN,
      edit_all: VENDOR_PAYMENTS_PERMISSIONS.EDIT_ALL,
      delete_own: VENDOR_PAYMENTS_PERMISSIONS.DELETE_OWN,
      delete_all: VENDOR_PAYMENTS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Finance Scenarios",
    accessKey: MODULE_ACCESS_PERMISSIONS.FINANCE_SCENARIOS,
    hasOwnConcept: true,
    permissions: {
      view_own: FINANCE_SCENARIOS_PERMISSIONS.VIEW_OWN,
      view_all: FINANCE_SCENARIOS_PERMISSIONS.VIEW_ALL,
      create: FINANCE_SCENARIOS_PERMISSIONS.CREATE,
      edit_own: FINANCE_SCENARIOS_PERMISSIONS.EDIT_OWN,
      edit_all: FINANCE_SCENARIOS_PERMISSIONS.EDIT_ALL,
      delete_own: FINANCE_SCENARIOS_PERMISSIONS.DELETE_OWN,
      delete_all: FINANCE_SCENARIOS_PERMISSIONS.DELETE_ALL,
    },
  },
];

export interface CollectionActionConfig {
  name: string;
  createKey: string;
  editKey: string;
}

export const OPS_COLLECTION_ACTIONS: CollectionActionConfig = {
  name: "Collections",
  createKey: "can_create_collections",
  editKey: "can_edit_collections",
};

export const OPS_MODULES: ModuleConfig[] = [
  {
    name: "Strategy",
    accessKey: MODULE_ACCESS_PERMISSIONS.STRATEGY,
    hasOwnConcept: true,
    permissions: {
      view_own: STRATEGY_PERMISSIONS.VIEW_OWN,
      view_all: STRATEGY_PERMISSIONS.VIEW_ALL,
      create: STRATEGY_PERMISSIONS.CREATE,
      edit_own: STRATEGY_PERMISSIONS.EDIT_OWN,
      edit_all: STRATEGY_PERMISSIONS.EDIT_ALL,
      delete_own: STRATEGY_PERMISSIONS.DELETE_OWN,
      delete_all: STRATEGY_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Projects",
    accessKey: MODULE_ACCESS_PERMISSIONS.PROJECTS,
    hasOwnConcept: true,
    permissions: {
      view_own: PROJECTS_PERMISSIONS.VIEW_OWN,
      view_all: PROJECTS_PERMISSIONS.VIEW_ALL,
      create: PROJECTS_PERMISSIONS.CREATE,
      edit_own: PROJECTS_PERMISSIONS.EDIT_OWN,
      edit_all: PROJECTS_PERMISSIONS.EDIT_ALL,
      delete_own: PROJECTS_PERMISSIONS.DELETE_OWN,
      delete_all: PROJECTS_PERMISSIONS.DELETE_ALL,
    },
  },
  {
    name: "Tasks",
    accessKey: MODULE_ACCESS_PERMISSIONS.TASKS,
    hasOwnConcept: true,
    permissions: {
      view_own: TASKS_PERMISSIONS.VIEW_OWN,
      view_all: TASKS_PERMISSIONS.VIEW_ALL,
      create: TASKS_PERMISSIONS.CREATE,
      edit_own: TASKS_PERMISSIONS.EDIT_OWN,
      edit_all: TASKS_PERMISSIONS.EDIT_ALL,
      delete_own: TASKS_PERMISSIONS.DELETE_OWN,
      delete_all: TASKS_PERMISSIONS.DELETE_ALL,
    },
  },
];

/**
 * Whiteboards access is membership-based (whiteboard/folder members), so the role
 * only decides whether the member additionally sees boards shared with the whole
 * organization. There is no create/edit/delete split.
 */
export const WHITEBOARDS_VIEW_ACTIONS = {
  name: "Whiteboards visibility",
  viewOwnKey: WHITEBOARDS_PERMISSIONS.VIEW_OWN,
  viewAllKey: WHITEBOARDS_PERMISSIONS.VIEW_ALL,
} as const;

/**
 * Presentations access is membership-based (presentation/folder members), so the
 * role only decides whether the member additionally sees decks shared with the
 * whole organization. There is no create/edit/delete split.
 */
export const PRESENTATIONS_VIEW_ACTIONS = {
  name: "Presentations visibility",
  viewOwnKey: PRESENTATIONS_PERMISSIONS.VIEW_OWN,
  viewAllKey: PRESENTATIONS_PERMISSIONS.VIEW_ALL,
} as const;

export interface ToolModuleConfig {
  name: string;
  key: ModuleAccessKey;
}

export const TOOL_MODULES: ToolModuleConfig[] = [
  { name: "Products", key: MODULE_ACCESS_PERMISSIONS.PRODUCTS },
  { name: "Payments", key: MODULE_ACCESS_PERMISSIONS.PAYMENTS },
  { name: "Campaigns", key: MODULE_ACCESS_PERMISSIONS.CAMPAIGNS },
  { name: "AI Assistant", key: MODULE_ACCESS_PERMISSIONS.AI_ASSISTANT },
  { name: "Automations", key: MODULE_ACCESS_PERMISSIONS.AUTOMATIONS },
  { name: "Analytics", key: MODULE_ACCESS_PERMISSIONS.ANALYTICS },
];

export const OPS_TOOL_MODULES: ToolModuleConfig[] = [
  { name: "Teams", key: MODULE_ACCESS_PERMISSIONS.TEAMS },
  { name: "Calendar", key: MODULE_ACCESS_PERMISSIONS.CALENDAR },
  { name: "Reports", key: MODULE_ACCESS_PERMISSIONS.REPORTS },
];

export const RESERVATIONS_MODULES: ModuleConfig[] = [
  {
    name: "Reservations",
    accessKey: MODULE_ACCESS_PERMISSIONS.RESERVATIONS,
    hasOwnConcept: true,
    permissions: {
      view_own: RESERVATIONS_PERMISSIONS.VIEW_OWN,
      view_all: RESERVATIONS_PERMISSIONS.VIEW_ALL,
      create: RESERVATIONS_PERMISSIONS.CREATE,
      edit_own: RESERVATIONS_PERMISSIONS.EDIT_OWN,
      edit_all: RESERVATIONS_PERMISSIONS.EDIT_ALL,
      delete_own: RESERVATIONS_PERMISSIONS.DELETE_OWN,
      delete_all: RESERVATIONS_PERMISSIONS.DELETE_ALL,
    },
  },
];

export const CONTRACTS_MODULES: ModuleConfig[] = [
  {
    name: "Contracts",
    accessKey: MODULE_ACCESS_PERMISSIONS.CONTRACTS,
    hasOwnConcept: true,
    permissions: {
      view_own: CONTRACTS_PERMISSIONS.VIEW_OWN,
      view_all: CONTRACTS_PERMISSIONS.VIEW_ALL,
      create: CONTRACTS_PERMISSIONS.CREATE,
      edit_own: CONTRACTS_PERMISSIONS.EDIT_OWN,
      edit_all: CONTRACTS_PERMISSIONS.EDIT_ALL,
      delete_own: CONTRACTS_PERMISSIONS.DELETE_OWN,
      delete_all: CONTRACTS_PERMISSIONS.DELETE_ALL,
    },
  },
];

export interface HelpdeskTicketScopeOption {
  value: HelpdeskTicketScope;
  label: string;
  description: string;
}

export const HELPDESK_TICKET_SCOPE_OPTIONS: HelpdeskTicketScopeOption[] = [
  {
    value: HELPDESK_TICKET_SCOPES.ALL,
    label: "All tickets",
    description: "Can see and work on every ticket in the organization.",
  },
  {
    value: HELPDESK_TICKET_SCOPES.GROUP,
    label: "Only my group's tickets",
    description:
      "Limited to tickets routed to their support groups, plus tickets assigned to them.",
  },
  {
    value: HELPDESK_TICKET_SCOPES.ASSIGNED,
    label: "Only tickets assigned to me",
    description: "Limited to tickets where they are the assigned agent.",
  },
];

export interface SectionConfig {
  name: string;
  key: SectionAccessKey;
}

export const SECTION_MODULES: SectionConfig[] = [
  { name: "CRM", key: SECTION_ACCESS_PERMISSIONS.CRM },
  { name: "Operations", key: SECTION_ACCESS_PERMISSIONS.OPERATIONS },
  { name: "Helpdesk", key: SECTION_ACCESS_PERMISSIONS.HELPDESK },
  { name: "Finance", key: SECTION_ACCESS_PERMISSIONS.FINANCE },
  { name: "Sites", key: SECTION_ACCESS_PERMISSIONS.SITES },
  { name: "Presentations", key: SECTION_ACCESS_PERMISSIONS.PRESENTATIONS },
  { name: "Contracts", key: SECTION_ACCESS_PERMISSIONS.CONTRACTS },
  { name: "Whiteboards", key: SECTION_ACCESS_PERMISSIONS.WHITEBOARDS },
  { name: "Events", key: SECTION_ACCESS_PERMISSIONS.EVENTS },
  { name: "Reservations", key: SECTION_ACCESS_PERMISSIONS.RESERVATIONS },
  { name: "Meetings", key: SECTION_ACCESS_PERMISSIONS.MEETINGS },
  { name: "Links", key: SECTION_ACCESS_PERMISSIONS.LINKS },
  { name: "Marketplace", key: SECTION_ACCESS_PERMISSIONS.MARKETPLACE },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const hasUnrestrictedAccess = (role: UserRole): boolean => {
  return role === "owner" || role === "admin";
};

export const getDefaultMemberPermissions = (): OrganizationPermissions => {
  return {
    // Section Access
    can_access_crm: true,
    can_access_operations: true,
    can_access_helpdesk: true,
    can_access_finance: true,
    can_access_sites: true,
    can_access_presentations: true,
    can_access_contracts: true,
    can_access_whiteboards: true,
    can_access_events: true,
    can_access_reservations: true,
    can_access_meetings: true,
    can_access_links: true,
    can_access_marketplace: true,

    // CRM Module Access
    can_access_conversations: true,
    can_access_deals: true,
    can_access_tickets: true,
    can_access_activities: true,
    can_access_contacts: true,
    can_access_companies: true,
    can_access_products: true,
    can_access_invoices: true,
    can_access_estimates: true,
    can_access_orders: true,
    can_access_payments: true,
    can_access_vendors: true,
    can_access_expenses: true,
    can_access_bills: true,
    can_access_vendor_payments: true,
    can_access_finance_scenarios: true,
    can_access_campaigns: true,
    can_access_ai_assistant: true,
    can_access_automations: true,
    can_access_analytics: true,

    // Whiteboards visibility
    can_view_own_whiteboards: true,
    can_view_all_whiteboards: true,
    can_view_own_presentations: true,
    can_view_all_presentations: true,

    // Operations Module Access
    can_access_strategy: true,
    can_access_projects: true,
    can_access_tasks: true,
    can_access_teams: true,
    can_access_calendar: true,
    can_access_reports: true,

    // Conversations
    can_view_own_conversations: true,
    can_view_all_conversations: true,
    can_create_conversations: true,
    can_edit_own_conversations: true,
    can_edit_all_conversations: true,
    can_delete_own_conversations: true,
    can_delete_all_conversations: true,

    // Deals
    can_view_own_deals: true,
    can_view_all_deals: true,
    can_create_deals: true,
    can_edit_own_deals: true,
    can_edit_all_deals: true,
    can_delete_own_deals: true,
    can_delete_all_deals: true,

    // Activities
    can_view_own_activities: true,
    can_view_all_activities: true,
    can_create_activities: true,
    can_edit_own_activities: true,
    can_edit_all_activities: true,
    can_delete_own_activities: true,
    can_delete_all_activities: true,

    // Reservations
    can_view_own_reservations: true,
    can_view_all_reservations: true,
    can_create_reservations: true,
    can_edit_own_reservations: true,
    can_edit_all_reservations: true,
    can_delete_own_reservations: true,
    can_delete_all_reservations: true,

    // Events
    can_view_own_events: true,
    can_view_all_events: true,
    can_create_events: true,
    can_edit_own_events: true,
    can_edit_all_events: true,
    can_delete_own_events: true,
    can_delete_all_events: true,

    // Contracts
    can_view_own_contracts: true,
    can_view_all_contracts: true,
    can_create_contracts: true,
    can_edit_own_contracts: true,
    can_edit_all_contracts: true,
    can_delete_own_contracts: true,
    can_delete_all_contracts: true,

    // Contacts
    can_view_own_contacts: true,
    can_view_all_contacts: true,
    can_create_contacts: true,
    can_edit_own_contacts: true,
    can_edit_all_contacts: true,
    can_delete_own_contacts: true,
    can_delete_all_contacts: true,

    // Companies
    can_view_own_companies: true,
    can_view_all_companies: true,
    can_create_companies: true,
    can_edit_own_companies: true,
    can_edit_all_companies: true,
    can_delete_own_companies: true,
    can_delete_all_companies: true,

    // Tickets
    can_view_own_tickets: true,
    can_view_all_tickets: true,
    can_create_tickets: true,
    can_edit_own_tickets: true,
    can_edit_all_tickets: true,
    can_delete_own_tickets: true,
    can_delete_all_tickets: true,

    // Invoices
    can_view_own_invoices: true,
    can_view_all_invoices: true,
    can_create_invoices: true,
    can_edit_own_invoices: true,
    can_edit_all_invoices: true,
    can_delete_own_invoices: true,
    can_delete_all_invoices: true,

    // Estimates
    can_view_own_estimates: true,
    can_view_all_estimates: true,
    can_create_estimates: true,
    can_edit_own_estimates: true,
    can_edit_all_estimates: true,
    can_delete_own_estimates: true,
    can_delete_all_estimates: true,

    // Orders
    can_view_own_orders: true,
    can_view_all_orders: true,
    can_create_orders: true,
    can_edit_own_orders: true,
    can_edit_all_orders: true,
    can_delete_own_orders: true,
    can_delete_all_orders: true,

    // Vendors
    can_view_own_vendors: true,
    can_view_all_vendors: true,
    can_create_vendors: true,
    can_edit_own_vendors: true,
    can_edit_all_vendors: true,
    can_delete_own_vendors: true,
    can_delete_all_vendors: true,

    // Expenses
    can_view_own_expenses: true,
    can_view_all_expenses: true,
    can_create_expenses: true,
    can_edit_own_expenses: true,
    can_edit_all_expenses: true,
    can_delete_own_expenses: true,
    can_delete_all_expenses: true,

    // Bills
    can_view_own_bills: true,
    can_view_all_bills: true,
    can_create_bills: true,
    can_edit_own_bills: true,
    can_edit_all_bills: true,
    can_delete_own_bills: true,
    can_delete_all_bills: true,

    // Vendor Payments
    can_view_own_vendor_payments: true,
    can_view_all_vendor_payments: true,
    can_create_vendor_payments: true,
    can_edit_own_vendor_payments: true,
    can_edit_all_vendor_payments: true,
    can_delete_own_vendor_payments: true,
    can_delete_all_vendor_payments: true,
    can_view_own_finance_scenarios: true,
    can_view_all_finance_scenarios: true,
    can_create_finance_scenarios: true,
    can_edit_own_finance_scenarios: true,
    can_edit_all_finance_scenarios: true,
    can_delete_own_finance_scenarios: true,
    can_delete_all_finance_scenarios: true,

    // Strategy
    can_view_own_strategy: true,
    can_view_all_strategy: true,
    can_create_strategy: true,
    can_edit_own_strategy: true,
    can_edit_all_strategy: true,
    can_delete_own_strategy: true,
    can_delete_all_strategy: true,

    can_create_collections: true,
    can_edit_collections: true,

    // Projects
    can_view_own_projects: true,
    can_view_all_projects: true,
    can_create_projects: true,
    can_edit_own_projects: true,
    can_edit_all_projects: true,
    can_delete_own_projects: true,
    can_delete_all_projects: true,

    // Tasks
    can_view_own_tasks: true,
    can_view_all_tasks: true,
    can_create_tasks: true,
    can_edit_own_tasks: true,
    can_edit_all_tasks: true,
    can_delete_own_tasks: true,
    can_delete_all_tasks: true,

    // Helpdesk
    helpdesk_ticket_scope: HELPDESK_TICKET_SCOPES.ALL,
    can_manage_helpdesk_settings: true,
  };
};

export const getHelpdeskTicketScope = (
  permissions: OrganizationPermissions | undefined,
): HelpdeskTicketScope => {
  const scope = permissions?.helpdesk_ticket_scope;

  if (
    scope === HELPDESK_TICKET_SCOPES.GROUP ||
    scope === HELPDESK_TICKET_SCOPES.ASSIGNED
  ) {
    return scope;
  }

  return HELPDESK_TICKET_SCOPES.ALL;
};

export const getPermission = (
  permissions: OrganizationPermissions | undefined,
  key: string | undefined,
): boolean | string | undefined => {
  if (!permissions || !key) return undefined;
  return permissions[key];
};

export const isPermissionGranted = (
  permissions: OrganizationPermissions | undefined,
  key: string | undefined,
): boolean => {
  if (!permissions || !key) return true;
  return permissions[key] !== false;
};

export const getPermissionLabel = (permission: string): string => {
  const labels: Record<string, string> = {
    // Section Access
    can_access_crm: "Access CRM",
    can_access_operations: "Access Operations",
    can_access_helpdesk: "Access Helpdesk",
    can_access_finance: "Access Finance",
    can_access_sites: "Access Sites",
    can_access_presentations: "Access Presentations",
    can_access_contracts: "Access Contracts",
    can_access_whiteboards: "Access Whiteboards",
    can_access_events: "Access Events",
    can_access_meetings: "Access Meetings",
    can_access_links: "Access Links",
    can_access_marketplace: "Access Marketplace",

    // Helpdesk
    can_manage_helpdesk_settings: "Manage Helpdesk Settings",
    helpdesk_ticket_scope: "Helpdesk Ticket Scope",

    // Module Access
    can_access_conversations: "Access Conversations",
    can_access_deals: "Access Deals",
    can_access_tickets: "Access Tickets",
    can_access_activities: "Access Activities",
    can_access_contacts: "Access Contacts",
    can_access_companies: "Access Companies",
    can_access_products: "Access Products",
    can_access_invoices: "Access Invoices",
    can_access_estimates: "Access Estimates",
    can_access_orders: "Access Orders",
    can_access_payments: "Access Payments",
    can_access_vendors: "Access Vendors",
    can_access_expenses: "Access Expenses",
    can_access_bills: "Access Bills",
    can_access_vendor_payments: "Access Vendor Payments",
    can_access_finance_scenarios: "Access Finance Scenarios",
    can_access_campaigns: "Access Campaigns",
    can_access_ai_assistant: "Access AI Assistant",
    can_access_automations: "Access Automations",
    can_access_analytics: "Access Analytics",
    can_access_reservations: "Access Reservations",
    can_view_own_whiteboards: "View Own Whiteboards",
    can_view_all_whiteboards: "View All Whiteboards",
    can_view_own_presentations: "View Own Presentations",
    can_view_all_presentations: "View All Presentations",
    can_access_strategy: "Access Strategy",
    can_access_projects: "Access Projects",
    can_access_tasks: "Access Tasks",
    can_access_teams: "Access Teams",
    can_access_calendar: "Access Calendar",
    can_access_reports: "Access Reports",

    // View
    can_view_own_conversations: "View Own Conversations",
    can_view_all_conversations: "View All Conversations",
    can_view_own_deals: "View Own Deals",
    can_view_all_deals: "View All Deals",
    can_view_own_activities: "View Own Activities",
    can_view_all_activities: "View All Activities",
    can_view_own_reservations: "View Own Reservations",
    can_view_all_reservations: "View All Reservations",
    can_view_own_events: "View Own Events",
    can_view_all_events: "View All Events",
    can_view_own_contracts: "View Own Contracts",
    can_view_all_contracts: "View All Contracts",
    can_view_own_contacts: "View Own Contacts",
    can_view_all_contacts: "View All Contacts",
    can_view_own_companies: "View Own Companies",
    can_view_all_companies: "View All Companies",
    can_view_own_tickets: "View Own Tickets",
    can_view_all_tickets: "View All Tickets",
    can_view_own_invoices: "View Own Invoices",
    can_view_all_invoices: "View All Invoices",
    can_view_own_estimates: "View Own Estimates",
    can_view_all_estimates: "View All Estimates",
    can_view_own_orders: "View Own Orders",
    can_view_all_orders: "View All Orders",
    can_view_own_vendors: "View Own Vendors",
    can_view_all_vendors: "View All Vendors",
    can_view_own_expenses: "View Own Expenses",
    can_view_all_expenses: "View All Expenses",
    can_view_own_bills: "View Own Bills",
    can_view_all_bills: "View All Bills",
    can_view_own_vendor_payments: "View Own Vendor Payments",
    can_view_all_vendor_payments: "View All Vendor Payments",
    can_view_own_strategy: "View Own Strategy",
    can_view_all_strategy: "View All Strategy",
    can_view_own_projects: "View Own Projects",
    can_view_all_projects: "View All Projects",
    can_view_own_tasks: "View Own Tasks",
    can_view_all_tasks: "View All Tasks",

    // Create
    can_create_conversations: "Create Conversations",
    can_create_deals: "Create Deals",
    can_create_activities: "Create Activities",
    can_create_reservations: "Create Reservations",
    can_create_events: "Create Events",
    can_create_contracts: "Create Contracts",
    can_create_contacts: "Create Contacts",
    can_create_companies: "Create Companies",
    can_create_tickets: "Create Tickets",
    can_create_invoices: "Create Invoices",
    can_create_estimates: "Create Estimates",
    can_create_orders: "Create Orders",
    can_create_vendors: "Create Vendors",
    can_create_expenses: "Create Expenses",
    can_create_bills: "Create Bills",
    can_create_vendor_payments: "Create Vendor Payments",
    can_create_strategy: "Create Strategy",
    can_create_collections: "Create Collections",
    can_create_projects: "Create Projects",
    can_create_tasks: "Create Tasks",

    // Edit
    can_edit_own_conversations: "Edit Own Conversations",
    can_edit_all_conversations: "Edit All Conversations",
    can_edit_own_deals: "Edit Own Deals",
    can_edit_all_deals: "Edit All Deals",
    can_edit_own_activities: "Edit Own Activities",
    can_edit_all_activities: "Edit All Activities",
    can_edit_own_reservations: "Edit Own Reservations",
    can_edit_all_reservations: "Edit All Reservations",
    can_edit_own_events: "Edit Own Events",
    can_edit_all_events: "Edit All Events",
    can_edit_own_contracts: "Edit Own Contracts",
    can_edit_all_contracts: "Edit All Contracts",
    can_edit_own_contacts: "Edit Own Contacts",
    can_edit_all_contacts: "Edit All Contacts",
    can_edit_own_companies: "Edit Own Companies",
    can_edit_all_companies: "Edit All Companies",
    can_edit_own_tickets: "Edit Own Tickets",
    can_edit_all_tickets: "Edit All Tickets",
    can_edit_own_invoices: "Edit Own Invoices",
    can_edit_all_invoices: "Edit All Invoices",
    can_edit_own_estimates: "Edit Own Estimates",
    can_edit_all_estimates: "Edit All Estimates",
    can_edit_own_orders: "Edit Own Orders",
    can_edit_all_orders: "Edit All Orders",
    can_edit_own_vendors: "Edit Own Vendors",
    can_edit_all_vendors: "Edit All Vendors",
    can_edit_own_expenses: "Edit Own Expenses",
    can_edit_all_expenses: "Edit All Expenses",
    can_edit_own_bills: "Edit Own Bills",
    can_edit_all_bills: "Edit All Bills",
    can_edit_own_vendor_payments: "Edit Own Vendor Payments",
    can_edit_all_vendor_payments: "Edit All Vendor Payments",
    can_edit_own_strategy: "Edit Own Strategy",
    can_edit_all_strategy: "Edit All Strategy",
    can_edit_collections: "Edit Collections",
    can_edit_own_projects: "Edit Own Projects",
    can_edit_all_projects: "Edit All Projects",
    can_edit_own_tasks: "Edit Own Tasks",
    can_edit_all_tasks: "Edit All Tasks",

    // Delete
    can_delete_own_conversations: "Delete Own Conversations",
    can_delete_all_conversations: "Delete All Conversations",
    can_delete_own_deals: "Delete Own Deals",
    can_delete_all_deals: "Delete All Deals",
    can_delete_own_activities: "Delete Own Activities",
    can_delete_all_activities: "Delete All Activities",
    can_delete_own_reservations: "Delete Own Reservations",
    can_delete_all_reservations: "Delete All Reservations",
    can_delete_own_events: "Delete Own Events",
    can_delete_all_events: "Delete All Events",
    can_delete_own_contracts: "Delete Own Contracts",
    can_delete_all_contracts: "Delete All Contracts",
    can_delete_own_contacts: "Delete Own Contacts",
    can_delete_all_contacts: "Delete All Contacts",
    can_delete_own_companies: "Delete Own Companies",
    can_delete_all_companies: "Delete All Companies",
    can_delete_own_tickets: "Delete Own Tickets",
    can_delete_all_tickets: "Delete All Tickets",
    can_delete_own_invoices: "Delete Own Invoices",
    can_delete_all_invoices: "Delete All Invoices",
    can_delete_own_estimates: "Delete Own Estimates",
    can_delete_all_estimates: "Delete All Estimates",
    can_delete_own_orders: "Delete Own Orders",
    can_delete_all_orders: "Delete All Orders",
    can_delete_own_vendors: "Delete Own Vendors",
    can_delete_all_vendors: "Delete All Vendors",
    can_delete_own_expenses: "Delete Own Expenses",
    can_delete_all_expenses: "Delete All Expenses",
    can_delete_own_bills: "Delete Own Bills",
    can_delete_all_bills: "Delete All Bills",
    can_delete_own_vendor_payments: "Delete Own Vendor Payments",
    can_delete_all_vendor_payments: "Delete All Vendor Payments",
    can_view_own_finance_scenarios: "View Own Finance Scenarios",
    can_view_all_finance_scenarios: "View All Finance Scenarios",
    can_create_finance_scenarios: "Create Finance Scenarios",
    can_edit_own_finance_scenarios: "Edit Own Finance Scenarios",
    can_edit_all_finance_scenarios: "Edit All Finance Scenarios",
    can_delete_own_finance_scenarios: "Delete Own Finance Scenarios",
    can_delete_all_finance_scenarios: "Delete All Finance Scenarios",
    can_delete_own_strategy: "Delete Own Strategy",
    can_delete_all_strategy: "Delete All Strategy",
    can_delete_own_projects: "Delete Own Projects",
    can_delete_all_projects: "Delete All Projects",
    can_delete_own_tasks: "Delete Own Tasks",
    can_delete_all_tasks: "Delete All Tasks",
  };

  return labels[permission] || permission;
};

type HasPermissionFn = (orgId: number, permission: string) => boolean;

export function canViewDeals(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.DEALS)) {
    return false;
  }

  return (
    hasPermission(orgId, DEALS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, DEALS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateDeals(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewDeals(hasPermission, orgId) &&
    hasPermission(orgId, DEALS_PERMISSIONS.CREATE)
  );
}

export function canViewActivities(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.ACTIVITIES)) {
    return false;
  }

  return (
    hasPermission(orgId, ACTIVITIES_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, ACTIVITIES_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateActivities(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewActivities(hasPermission, orgId) &&
    hasPermission(orgId, ACTIVITIES_PERMISSIONS.CREATE)
  );
}

export function canViewTickets(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.TICKETS)) {
    return false;
  }

  return (
    hasPermission(orgId, TICKETS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, TICKETS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateTickets(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewTickets(hasPermission, orgId) &&
    hasPermission(orgId, TICKETS_PERMISSIONS.CREATE)
  );
}

export function canViewEstimates(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.ESTIMATES)) {
    return false;
  }

  return (
    hasPermission(orgId, ESTIMATES_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, ESTIMATES_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateEstimates(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewEstimates(hasPermission, orgId) &&
    hasPermission(orgId, ESTIMATES_PERMISSIONS.CREATE)
  );
}

export function canViewOrders(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.ORDERS)) {
    return false;
  }

  return (
    hasPermission(orgId, ORDERS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, ORDERS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateOrders(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewOrders(hasPermission, orgId) &&
    hasPermission(orgId, ORDERS_PERMISSIONS.CREATE)
  );
}

export function canViewReservations(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.RESERVATIONS)) {
    return false;
  }

  return (
    hasPermission(orgId, RESERVATIONS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, RESERVATIONS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateReservations(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewReservations(hasPermission, orgId) &&
    hasPermission(orgId, RESERVATIONS_PERMISSIONS.CREATE)
  );
}

export function canViewEvents(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.EVENTS)) {
    return false;
  }

  return (
    hasPermission(orgId, EVENTS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, EVENTS_PERMISSIONS.VIEW_ALL)
  );
}

export function canViewContracts(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.CONTRACTS)) {
    return false;
  }

  return (
    hasPermission(orgId, CONTRACTS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, CONTRACTS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateContracts(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewContracts(hasPermission, orgId) &&
    hasPermission(orgId, CONTRACTS_PERMISSIONS.CREATE)
  );
}

export function canEditContracts(
  hasPermission: HasPermissionFn,
  orgId: number,
  ownerUserId?: number | null,
  currentUserId?: number | null,
): boolean {
  if (!canViewContracts(hasPermission, orgId)) {
    return false;
  }

  if (hasPermission(orgId, CONTRACTS_PERMISSIONS.EDIT_ALL)) {
    return true;
  }

  if (!hasPermission(orgId, CONTRACTS_PERMISSIONS.EDIT_OWN)) {
    return false;
  }

  return (
    ownerUserId != null &&
    currentUserId != null &&
    ownerUserId === currentUserId
  );
}

export function canDeleteContracts(
  hasPermission: HasPermissionFn,
  orgId: number,
  ownerUserId?: number | null,
  currentUserId?: number | null,
): boolean {
  if (!canViewContracts(hasPermission, orgId)) {
    return false;
  }

  if (hasPermission(orgId, CONTRACTS_PERMISSIONS.DELETE_ALL)) {
    return true;
  }

  if (!hasPermission(orgId, CONTRACTS_PERMISSIONS.DELETE_OWN)) {
    return false;
  }

  return (
    ownerUserId != null &&
    currentUserId != null &&
    ownerUserId === currentUserId
  );
}

export function canEditContractResources(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewContracts(hasPermission, orgId) &&
    (hasPermission(orgId, CONTRACTS_PERMISSIONS.EDIT_OWN) ||
      hasPermission(orgId, CONTRACTS_PERMISSIONS.EDIT_ALL))
  );
}

export function canDeleteContractResources(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewContracts(hasPermission, orgId) &&
    (hasPermission(orgId, CONTRACTS_PERMISSIONS.DELETE_OWN) ||
      hasPermission(orgId, CONTRACTS_PERMISSIONS.DELETE_ALL))
  );
}

export function canViewProjects(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.PROJECTS)) {
    return false;
  }

  return (
    hasPermission(orgId, PROJECTS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, PROJECTS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateProjects(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewProjects(hasPermission, orgId) &&
    hasPermission(orgId, PROJECTS_PERMISSIONS.CREATE)
  );
}

export function canViewContacts(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.CONTACTS)) {
    return false;
  }

  return (
    hasPermission(orgId, CONTACTS_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, CONTACTS_PERMISSIONS.VIEW_ALL)
  );
}

export function hasOwnContactsOnlyAccess(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewContacts(hasPermission, orgId) &&
    !hasPermission(orgId, CONTACTS_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateContacts(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewContacts(hasPermission, orgId) &&
    hasPermission(orgId, CONTACTS_PERMISSIONS.CREATE)
  );
}

export function canViewCompanies(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.COMPANIES)) {
    return false;
  }

  return (
    hasPermission(orgId, COMPANIES_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, COMPANIES_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateCompanies(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewCompanies(hasPermission, orgId) &&
    hasPermission(orgId, COMPANIES_PERMISSIONS.CREATE)
  );
}

export function canViewInvoices(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  if (!hasPermission(orgId, MODULE_ACCESS_PERMISSIONS.INVOICES)) {
    return false;
  }

  return (
    hasPermission(orgId, INVOICES_PERMISSIONS.VIEW_OWN) ||
    hasPermission(orgId, INVOICES_PERMISSIONS.VIEW_ALL)
  );
}

export function canCreateInvoices(
  hasPermission: HasPermissionFn,
  orgId: number,
): boolean {
  return (
    canViewInvoices(hasPermission, orgId) &&
    hasPermission(orgId, INVOICES_PERMISSIONS.CREATE)
  );
}
